import { useEffect, useState } from "react";
import { setDone } from "../tasks/earlyDone";
import { useTranslation } from "react-i18next";
import { HelpLink } from "../app/HelpLink";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { BigClockPage } from "./BigClockPage";
import { X } from "lucide-react";
import { Button, Checkbox, Textarea } from "../ui";
import { useCoreDoc } from "../app/useCoreDoc";
import { useDocSelector, useDocState } from "../core/useDocSelector";
import { useNow } from "../core/useNow";
import { childrenOf, readTask } from "../core/tasks";
import { setNotes } from "../core/ops";
import { fmtElapsed } from "../core/format";
import { elapsedMs, readCurrent, setFocus, startTracking, stopTracking } from "../tracking/timelog";

/**
 * Focus mode (spec §9.6): one task, its timer, next action, subtasks and notes — nothing else.
 * The large clock for body doubling (§9.19) is Focus's large display (`?large=1`, `BigClockPage`); `/focus` alone
 * shows whatever runs, large (UI cleanup U2: the big view was a page of its own).
 * Focus is written (per person in a shared space, `setFocus`) so a kiosk can show "focusing on X since 14:02".
 */
import { pauseTimer, resumeTimer } from "../tracking/linked";
export function FocusPage() {
  const { t, i18n } = useTranslation();
  const { taskId } = useParams();
  const [params] = useSearchParams();
  // the large display (the former big view, `/clock`): `?large=1`, or `?body=1` from older links
  const large = params.get("large") === "1" || params.get("body") === "1";
  const navigate = useNavigate();
  const now = useNow();
  const { handle, workspaceId } = useCoreDoc();
  const { settled } = useDocState(handle);
  const task = useDocSelector(handle, (doc) => (taskId ? readTask(doc, taskId) : undefined), [taskId]);
  const children = useDocSelector(
    handle,
    (doc) => {
      const tk = taskId ? readTask(doc, taskId) : undefined;
      return tk
        ? childrenOf(doc, tk)
            .map((id) => readTask(doc, id))
            .filter((x) => x && !x.archivedAt)
        : [];
    },
    [taskId],
  );
  const current = useDocSelector(handle, (doc) => readCurrent(doc));
  const [notes, setNotesDraft] = useState<string | null>(null);

  useEffect(() => {
    if (!handle || !settled || handle.readOnly || !taskId || !workspaceId) return;
    const doc = handle.doc;
    setFocus(doc, taskId);
    if (readCurrent(doc)?.taskId !== taskId) void startTracking(doc, workspaceId, taskId);
    return () => setFocus(doc, null);
  }, [handle, settled, taskId, workspaceId]);

  // opened directly (a kiosk, a bookmark) there is no page to go back to: go home instead
  const leave = () => (window.history.length > 1 ? navigate(-1) : navigate("/"));
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // the large display handles Escape itself
      if (e.key === "Escape" && !large) leave();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (large) return <BigClockPage onSmaller={() => navigate(`/focus/${taskId}`, { replace: true })} />;
  if (!handle || !settled) return <p className="p-8 text-neutral-500">{t("session.loading")}</p>;
  if (!task) return <p className="p-8 text-neutral-500">{t("task.notFound")}</p>;
  const tracking = current?.taskId === task.id;
  const nextAction = typeof task.props.nextAction === "string" ? task.props.nextAction : "";

  return (
    <div
      className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col gap-6 px-4 py-10 mid:px-6 short:gap-3 short:py-4"
      data-testid="focus"
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
            {t("focus.title")}
          </p>
          <h1 className={"mt-1 text-[26px] font-semibold"}>{task.title}</h1>
          {nextAction && <p className="mt-1 text-neutral-500">{t("focus.next", { action: nextAction })}</p>}
        </div>
        <HelpLink page="guides/timers-and-pomodoro/" />
        <Button
          variant="ghost"
          size="sm"
          aria-label={t("focus.leave")}
          onClick={leave}
          className="w-[30px] px-0 touch:h-11 touch:w-11"
        >
          <X size={16} aria-hidden />
        </Button>
      </div>
      <p className="font-mono text-[3.2em] font-medium tabular-nums leading-none" data-testid="focus-elapsed">
        {tracking && current ? fmtElapsed(elapsedMs(current, now)) : "0:00"}
      </p>
      {!large && (
        <>
          {children && children.length > 0 && (
            <ul className="kss-surface divide-y divide-neutral-200 dark:divide-neutral-800">
              {children.map(
                (c) =>
                  c && (
                    <li key={c.id} className="flex items-center gap-3 px-4 py-2.5">
                      <Checkbox
                        checked={c.doneAt !== null}
                        label={c.doneAt ? t("task.undone") : t("task.done")}
                        onChange={(v) => {
                          setDone((fn) => fn(handle.doc), t, i18n.language, c, v);
                        }}
                      />
                      <span className={c.doneAt ? "text-neutral-500 line-through" : ""}>{c.title}</span>
                    </li>
                  ),
              )}
            </ul>
          )}
          <Textarea
            rows={8}
            aria-label={t("task.notes")}
            placeholder={t("notes.placeholder")}
            value={notes ?? task.notes}
            onChange={(e) => setNotesDraft(e.target.value)}
            onBlur={() => {
              if (notes !== null) {
                setNotes(handle.doc, task.id, notes);
                setNotesDraft(null);
              }
            }}
          />
        </>
      )}
      <div className="mt-auto flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          onClick={() =>
            void (tracking ? stopTracking(handle.doc) : startTracking(handle.doc, workspaceId!, task.id))
          }
        >
          {tracking ? t("tracking.stop") : t("tracking.start")}
        </Button>
        {tracking && current && (
          <Button
            variant="secondary"
            onClick={() =>
              void (current.pausedAt ? resumeTimer(handle.doc, workspaceId!) : pauseTimer(handle.doc))
            }
            data-testid="focus-pause"
          >
            {current.pausedAt ? t("tracking.resume") : t("tracking.pause")}
          </Button>
        )}
        <Button
          onClick={() => {
            if (!setDone((fn) => fn(handle.doc), t, i18n.language, task, true)) return;
            void stopTracking(handle.doc);
            leave();
          }}
        >
          {t("focus.doneAndLeave")}
        </Button>
        <Button
          variant="ghost"
          onClick={() => navigate(`/focus/${task.id}?large=1`)}
          data-testid="focus-big-view"
        >
          {t("focus.bodyDouble")}
        </Button>
      </div>
    </div>
  );
}
